import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Divider, Link, Stack, Step, StepLabel, Stepper, TextField, Typography } from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';

import CustomButton from 'src/components/atoms/buttons/CustomButton';
import PageHeader from 'src/components/organisms/PageHeader';
import ContentCardsContainer, { ContentCard } from 'src/components/organisms/ContentCard';
import Footer from 'src/components/molecules/footer/Footer';
import useRequest from 'src/hooks/api/useRequest';
import { confirm2FASetup, get2FASetup, invalidate2FA } from 'src/utils/apiRequests';
import { MFA_ENABLED } from 'src/utils/enviromentValues';
import { useMessageContext } from 'src/contexts/MessageContext';

type RequestStatus = string | number | null;

const MFA_STEPS = ['Start', 'Scan QR', 'Confirm', 'Enabled'];

const isSuccessfulStatus = (status: RequestStatus) => {
  if (typeof status === 'number') return status >= 200 && status < 300;
  return status === 'success';
};

const isFinishedStatus = (status: RequestStatus) => status !== null && status !== undefined && status !== 'idle' && status !== 'loading';

const getApiMessage = (value: any): string | null => {
  if (!value) return null;

  if (typeof value === 'string') {
    const trimmed = value.trim();

    try {
      return getApiMessage(JSON.parse(trimmed)) ?? trimmed;
    } catch {
      return trimmed;
    }
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const message = getApiMessage(item);
      if (message) return message;
    }

    return null;
  }

  if (typeof value !== 'object') return null;

  const directMessage = value.Message ?? value.message;
  if (typeof directMessage === 'string' && directMessage.trim()) return directMessage.trim();

  const candidates = [value.data, value.result, value.response, value.error, value.errors];

  for (const candidate of candidates) {
    const message = getApiMessage(candidate);
    if (message) return message;
  }

  return null;
};

const normalizeApiMessage = (message: string | null) => {
  return message?.toLowerCase().replace(/\s+/g, ' ').trim() ?? '';
};

const isPendingSetupMessage = (message: string | null) => {
  const value = normalizeApiMessage(message);
  return value.includes('two factor authentication for this user is in a pending state') && value.includes('waiting to be confirmed');
};

const isAlreadyActivatedMessage = (message: string | null) => {
  const value = normalizeApiMessage(message);
  return value.includes('two factor authentication is already activated for this user');
};

const findOtpAuthUri = (value: any): string | null => {
  if (!value) return null;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('otpauth://')) return trimmed;
    return trimmed.match(/otpauth:\/\/[^\s"']+/)?.[0] ?? null;
  }

  if (typeof value !== 'object') return null;

  const candidates = [
    value.OTPAuthUri, value.OTPAuthURI, value.OTPAuthUrl, value.OTPAuthURL,
    value.otpAuthUri, value.otpAuthUrl, value.otpauthUri, value.otpauthUrl,
    value.otpauth_uri, value.otpauth_url, value.uri, value.url, value.data, value.result
  ];

  for (const candidate of candidates) {
    const found = findOtpAuthUri(candidate);
    if (found) return found;
  }

  return null;
};

const getSecretFromOtpAuthUri = (otpAuthUri: string | null) => {
  if (!otpAuthUri) return null;

  try {
    return new URL(otpAuthUri).searchParams.get('secret');
  } catch {
    return null;
  }
};

function SetupMFA() {
  const { t }: { t: any } = useTranslation();
  const navigate = useNavigate();
  const { updateMessageStatus } = useMessageContext();

  const [activeStep, setActiveStep] = useState(0);
  const [OTPCode, setOTPCode] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [flowError, setFlowError] = useState<string | null>(null);
  const [pendingSetupDetected, setPendingSetupDetected] = useState(false);
  const [existingMfaDetected, setExistingMfaDetected] = useState(false);
  const [setupRequested, setSetupRequested] = useState(false);

  const [setupResult, setSetupRequest, abortSetup, setupLoading, setupStatus] = useRequest();
  const [confirmResult, setConfirmRequest, abortConfirm, confirmLoading, confirmStatus] = useRequest();
  const [invalidateResult, setInvalidateRequest, abortInvalidate, invalidateLoading, invalidateStatus] = useRequest();

  const otpAuthUri = useMemo(() => findOtpAuthUri(setupResult), [setupResult]);
  const secret = useMemo(() => getSecretFromOtpAuthUri(otpAuthUri), [otpAuthUri]);

  useEffect(() => {
    if (!MFA_ENABLED) navigate('/');

    return () => {
      abortSetup();
      abortConfirm();
      abortInvalidate();
    };
  }, []);

  useEffect(() => {
    if (!setupRequested || setupLoading || !isFinishedStatus(setupStatus)) return;

    const apiMessage = getApiMessage(setupResult);

    setSetupRequested(false);

    // Check API state messages BEFORE checking HTTP success.
    // These responses may still be returned with a 2xx status.
    if (isAlreadyActivatedMessage(apiMessage)) {
      setExistingMfaDetected(true);
      setPendingSetupDetected(false);
      setFlowError(null);
      setActiveStep(3);
      return;
    }

    if (isPendingSetupMessage(apiMessage)) {
      setExistingMfaDetected(false);
      setPendingSetupDetected(true);
      setFlowError(null);
      setActiveStep(2);
      return;
    }

    if (isSuccessfulStatus(setupStatus)) {
      setExistingMfaDetected(false);
      setPendingSetupDetected(false);
      setFlowError(null);

      if (otpAuthUri) {
        setActiveStep(1);
      } else {
        setFlowError(t('The MFA setup response did not contain an otpauth URL.'));
      }

      return;
    }

    setFlowError(apiMessage || t('Could not start MFA setup.'));
  }, [setupRequested, setupLoading, setupStatus, setupResult, otpAuthUri]);

  useEffect(() => {
    if (confirmLoading || !isFinishedStatus(confirmStatus) || !isSuccessfulStatus(confirmStatus)) return;

    setOTPCode('');
    setLocalError(null);
    setFlowError(null);
    setPendingSetupDetected(false);
    setExistingMfaDetected(false);
    setActiveStep(3);

    updateMessageStatus({
      requestUrl: '/api/user-accounts/2fa/confirm',
      status: 'success',
      message: 'Multi-factor authentication has been enabled successfully.',
      logLevel: ['success']
    });
  }, [confirmLoading, confirmStatus]);

  useEffect(() => {
    if (invalidateLoading || !isFinishedStatus(invalidateStatus) || !isSuccessfulStatus(invalidateStatus)) return;

    setActiveStep(0);
    setOTPCode('');
    setLocalError(null);
    setFlowError(null);
    setPendingSetupDetected(false);
    setExistingMfaDetected(false);
    setSetupRequested(false);

    updateMessageStatus({
      requestUrl: '/api/user-accounts/2fa/invalidate',
      status: 'success',
      message: 'Multi-factor authentication has been removed.',
      logLevel: ['success']
    });
  }, [invalidateLoading, invalidateStatus]);

  const confirmError = !confirmLoading && isFinishedStatus(confirmStatus) && !isSuccessfulStatus(confirmStatus)
    ? getApiMessage(confirmResult) || t('The authentication code was not accepted.')
    : null;

  const invalidateError = !invalidateLoading && isFinishedStatus(invalidateStatus) && !isSuccessfulStatus(invalidateStatus)
    ? getApiMessage(invalidateResult) || t('Could not remove MFA.')
    : null;

  const handleEnable = () => {
    setOTPCode('');
    setLocalError(null);
    setFlowError(null);
    setPendingSetupDetected(false);
    setExistingMfaDetected(false);
    setSetupRequested(true);
    setSetupRequest(get2FASetup());
  };

  const handleCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setOTPCode(event.target.value.replace(/\D/g, '').slice(0, 6));
    setLocalError(null);
  };

  const handleConfirm = () => {
    if (!/^\d{6}$/.test(OTPCode)) {
      setLocalError(t('Enter the 6-digit code from your authenticator app.'));
      return;
    }

    setLocalError(null);
    setConfirmRequest(confirm2FASetup(OTPCode));
  };

  const handleDisable = () => {
    if (invalidateLoading) return;
    if (!window.confirm(t('Remove multi-factor authentication from this account?'))) return;

    setInvalidateRequest(invalidate2FA());
  };

  return (
    <>
      <PageHeader {...{
        title: "Multi-factor authentication",
        subTitle: "Set up and manage multi-factor authentication for your account.",
        components: [
          { component: <CustomButton variant="outlined" title="Disable MFA" icon={<DeleteForeverIcon />} onClick={handleDisable} isSubmitting={invalidateLoading} /> }
        ]
      }} />

      <ContentCardsContainer>
        <ContentCard cardId="mfaSetup" cardHeader="MFA setup" md={12}>
          <Box sx={{ width: '100%' }}>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ width: '100%', mb: 4 }}>
              {MFA_STEPS.map(label => <Step key={label} sx={{ flex: 1 }}><StepLabel>{t(label)}</StepLabel></Step>)}
            </Stepper>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {invalidateError && <Alert severity="error" sx={{ mb: 3 }}>{invalidateError}</Alert>}

          {activeStep === 0 && (
            <Box sx={{ maxWidth: 700, mx: 'auto', textAlign: 'center', py: 3 }}>
              <SecurityIcon color="primary" sx={{ fontSize: 56, mb: 2 }} />

              <Typography variant="h3" sx={{ mb: 2 }}>{t('Enable multi-factor authentication')}</Typography>

              <Typography color="text.secondary" sx={{ mb: 3 }}>
                {t('Protect your account with an additional authentication code. Setup takes only a few steps: generate a QR code, scan it with an authenticator app, and confirm the generated code.')}
              </Typography>

              {flowError && <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>{flowError}</Alert>}

              <Button size="large" variant="contained" startIcon={setupLoading ? <CircularProgress size="1rem" /> : <SecurityIcon />} onClick={handleEnable} disabled={setupLoading}>
                {setupLoading ? t('Starting setup...') : t('Enable MFA')}
              </Button>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                {t('If MFA is already configured and you want to replace it, use Disable MFA at the top of the page first.')}
              </Typography>
            </Box>
          )}

          {activeStep === 1 && otpAuthUri && (
            <Box sx={{ maxWidth: 750, mx: 'auto' }}>
              <Typography variant="h3" sx={{ mb: 1 }}>{t('Scan the QR code')}</Typography>

              <Typography color="text.secondary" sx={{ mb: 3 }}>
                {t('Open your authenticator app and scan the QR code below. The app will add your account and begin generating 6-digit authentication codes.')}
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <QRCodeSVG value={otpAuthUri} size={260} level="M" includeMargin />
              </Box>

              {secret && (
                <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1, mb: 3 }}>
                  <Typography variant="body2" color="text.secondary">{t('Cannot scan the QR code? Enter this setup key manually in your authenticator app:')}</Typography>
                  <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, wordBreak: 'break-all', mt: 1 }}>{secret}</Typography>
                </Box>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end">
                <Button size="large" variant="contained" endIcon={<ArrowForwardIcon />} onClick={() => setActiveStep(2)}>
                  {t('I have scanned the QR code')}
                </Button>
              </Stack>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                {t('If you want to cancel this setup and remove the pending MFA configuration, use Disable MFA at the top of the page.')}
              </Typography>
            </Box>
          )}

          {activeStep === 2 && (
            <Box sx={{ maxWidth: 650, mx: 'auto' }}>
              <Typography variant="h3" sx={{ mb: 1 }}>{t('Confirm your authenticator')}</Typography>

              {pendingSetupDetected ? (
                <Alert severity="info" sx={{ mb: 3 }}>
                  {t('An MFA setup is already waiting to be confirmed. Enter the 6-digit code from the authenticator app used when the setup was started.')}
                </Alert>
              ) : (
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  {t('Enter the current 6-digit code from your authenticator app to confirm that setup was completed correctly.')}
                </Typography>
              )}

              <TextField
                fullWidth
                autoFocus
                label={t('Authentication code')}
                value={OTPCode}
                onChange={handleCodeChange}
                inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6 }}
                error={Boolean(localError || confirmError)}
                helperText={localError || confirmError || t('Enter the current 6-digit code from your authenticator app.')}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                {!pendingSetupDetected && otpAuthUri && <Button variant="outlined" onClick={() => setActiveStep(1)} disabled={confirmLoading}>{t('Back to QR code')}</Button>}

                <Button size="large" variant="contained" startIcon={confirmLoading ? <CircularProgress size="1rem" /> : <CheckCircleOutlineIcon />} onClick={handleConfirm} disabled={OTPCode.length !== 6 || confirmLoading}>
                  {confirmLoading ? t('Confirming...') : t('Confirm and enable MFA')}
                </Button>
              </Stack>

              {pendingSetupDetected && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                  {t('If you no longer have the authenticator from the previous setup attempt, use Disable MFA above, then start again to generate a new QR code.')}
                </Typography>
              )}
            </Box>
          )}

          {activeStep === 3 && (
            <Box sx={{ maxWidth: 700, mx: 'auto', textAlign: 'center', py: 4 }}>
              <CheckCircleOutlineIcon color="success" sx={{ fontSize: 90, mb: 2 }} />

              <Typography variant="h2" sx={{ mb: 1 }}>{t('MFA is enabled')}</Typography>

              <Typography color="text.secondary" sx={{ mb: 4 }}>
                {existingMfaDetected
                  ? t('Multi-factor authentication is already enabled for your account.')
                  : t('Multi-factor authentication has been enabled successfully for your account.')}
              </Typography>

              <Button size="large" color="error" variant="outlined" startIcon={invalidateLoading ? <CircularProgress size="1rem" /> : <DeleteForeverIcon />} onClick={handleDisable} disabled={invalidateLoading}>
                {invalidateLoading ? t('Removing MFA...') : t('Remove MFA')}
              </Button>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                {t('Removing MFA will disable authenticator-code verification for this account. You can set it up again later.')}
              </Typography>
            </Box>
          )}
        </ContentCard>

        <ContentCard cardId="mfaInformation" cardHeader="How MFA works" md={12}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 4 }}>
            <Box>
              <Typography variant="h4" sx={{ mb: 1 }}>1. {t('Install an authenticator')}</Typography>

              <Typography color="text.secondary" sx={{ mb: 2 }}>
                {t('Install a TOTP-compatible authenticator app on your phone or another trusted device.')}
              </Typography>

              <Stack spacing={1}>
                <Link href="https://support.google.com/accounts/answer/1066447" target="_blank" rel="noopener noreferrer">{t('Google Authenticator')}</Link>
                <Link href="https://support.microsoft.com/en-US/authenticator/download-microsoft-authenticator" target="_blank" rel="noopener noreferrer">{t('Microsoft Authenticator')}</Link>
              </Stack>
            </Box>

            <Box>
              <Typography variant="h4" sx={{ mb: 1 }}>2. {t('Scan the QR code')}</Typography>

              <Typography color="text.secondary">
                {t('The QR code contains the setup information your authenticator needs. Scan it from the app, or enter the setup key manually if scanning is unavailable.')}
              </Typography>
            </Box>

            <Box>
              <Typography variant="h4" sx={{ mb: 1 }}>3. {t('Confirm the code')}</Typography>

              <Typography color="text.secondary">
                {t('Your authenticator will generate a rotating 6-digit code. Enter one of these codes here to finish enabling MFA.')}
              </Typography>
            </Box>
          </Box>
        </ContentCard>
      </ContentCardsContainer>

      <Footer />
    </>
  );
}

export default SetupMFA;